import { TUNING } from '~/config/tuning'
import { BOSS_NAMES, ELITE_CHAMPIONS, MISSION_NAMES } from '~/data/missions'
// Explicit (not auto-) imports so the rarity simulation can bundle this
// module outside of Nuxt.
import { emptyStats, gauntletRampMult, UNSTABLE_GROW_SECONDS, UNSTABLE_ROTATE_SECONDS } from '~/utils/battle'
import { rand, makeId, randInt, shuffle, weightedPick } from '~/utils/random'
import { weeklyExpireMult, weeklyReqMult } from '~/data/weeklyMods'
import {
  ELEMENTS,
  type ElementKey,
  type MapNode,
  type Mission,
  type MissionTier,
  type Stats,
} from '~/types/game'

export type CombatNodeType = keyof typeof TUNING.encounters.byNodeType

export function isCombatNode(node: MapNode): boolean {
  return node.type in TUNING.encounters.byNodeType
}

/** Basalt (v0.61): the only boss with an expiration clock — race it.
 * v0.99 (per Javi): 150 → 100, a real race now. */
export const GOLEM_EXPIRE_SECONDS = 100
/** Basalt's crystal ring: five up at once, quick re-rolls when smashed. */
export const GOLEM_CRYSTAL_CAP = 5
export const GOLEM_CRYSTAL_RESPAWN_SECONDS = 3
/** After a LOST fight against Basalt the ring refills sluggishly. */
export const GOLEM_CRYSTAL_RESPAWN_SLOW_SECONDS = 10
/** Dr. Vex (v0.63, faster in v0.80): squashed meddling reappears FAST. */
export const SCIENTIST_MEDDLE_RESPAWN_SECONDS = 3
/**
 * ⏳ The Amalgam runs a clock too (v3.72, per Javi: "I was able to farm hp
 * back by killing dr.vex").
 *
 * Vex's meddling respawns every 3s forever and pays out like any other
 * mission, so a roster carrying success-healing (Field Medic, Green
 * Thumb…) could camp the throne room and net HP indefinitely — the node
 * had no deadline at all. A fuse gives it one: let it lapse and the
 * Amalgam reforms at full strength AND takes its expiry bite out of you,
 * which at act-3 boss damage is far more than any amount of meddling can
 * heal back.
 *
 * 240s where Basalt gets 100: the Amalgam's fight alone is 5.5× a normal
 * boss (71-94s before travel), so a shorter fuse would lapse before a
 * single honest attempt could land.
 */
export const SCIENTIST_EXPIRE_SECONDS = 240

function rollTier(node: MapNode): MissionTier {
  const weights = TUNING.encounters.byNodeType[node.type as CombatNodeType].tierWeights
  const tier = weightedPick(Object.entries(weights) as [MissionTier, number][])
  // Deep-map nodes fight one tier up.
  if (node.preview?.tier === 'hard') {
    if (tier === 'easy') return 'medium'
    if (tier === 'medium') return 'hard'
  }
  return tier
}

/** Picks the mission's required elements, favoring the node's focus. */
function chooseElements(elementCount: number, focus: ElementKey[], focusBias: number): ElementKey[] {
  const focusPool = shuffle(focus)
  const otherPool = shuffle(ELEMENTS.filter(e => !focus.includes(e)))
  const chosen: ElementKey[] = []
  for (let i = 0; i < Math.min(elementCount, ELEMENTS.length); i++) {
    const fromFocus = focusPool.length > 0 && (otherPool.length === 0 || rand() < focusBias)
    chosen.push(fromFocus ? focusPool.pop()! : otherPool.pop()!)
  }
  return chosen
}

/** Splits `total` requirement points across the chosen elements. */
function rollRequirements(total: number, chosen: ElementKey[]) {
  const required = emptyStats()
  // Everyone gets 1, the rest lands randomly.
  for (const element of chosen) required[element] = 1
  for (let i = 0; i < total - chosen.length; i++) {
    required[chosen[randInt(0, chosen.length - 1)]!]++
  }
  return required
}

/** 📱 M3: a pinned name wins over the rolled one. The roll is still made,
 * so every draw after it lands where it always did. */
function pinnedName(rolled: string, pinned: string | undefined): string {
  return pinned ?? rolled
}

function rollName(
  tier: MissionTier,
  required: ReturnType<typeof rollRequirements>,
  nodeType?: string,
  forceChampion = false,
): string {
  if (tier === 'boss') return BOSS_NAMES[randInt(0, BOSS_NAMES.length - 1)]!
  const dominant = ELEMENTS.reduce((a, b) => (required[b] > required[a] ? b : a))
  // 👑 Elite nodes' HARD missions can be the named champions (v2.68) —
  // same fight, but the one that hurts most has a face. See
  // ELITE_CHAMPIONS. v3.80: only `championChance` of them, so the eight
  // are worth chasing rather than guaranteed several times a run.
  // 🚩 v6.00 (per Javi): a banner node FORCES its champion — the
  // second spawn wears the face, no dice about it.
  const champion = forceChampion || (nodeType === 'elite'
    && tier === 'hard'
    && rand() < TUNING.encounters.championChance)
  const names = champion ? ELITE_CHAMPIONS[dominant] : MISSION_NAMES[dominant]
  return names[randInt(0, names.length - 1)]!
}

/** Requirement/damage/XP multiplier for a mission's distance from home. */
export function dangerForDistance(distance: number): number {
  const { minDistance, maxDistance, dangerMin, dangerMax } = TUNING.travel
  const t = Math.min(1, Math.max(0, (distance - minDistance) / (maxDistance - minDistance)))
  return dangerMin + t * (dangerMax - dangerMin)
}

/**
 * Qualitative distance from home base, shown instead of travel times:
 * the player reads near/far, not seconds.
 */
export function distanceBand(distance: number): 'near' | 'mid' | 'far' {
  const { minDistance, maxDistance } = TUNING.travel
  const t = (distance - minDistance) / (maxDistance - minDistance)
  return t < 1 / 3 ? 'near' : t < 2 / 3 ? 'mid' : 'far'
}

/**
 * Override hooks for the difficulty simulations (scripts/difficulty.ts).
 * The live game never passes options, so defaults come from TUNING:
 * per-act focusBias and the act-aware final requirement bands.
 */
export interface MissionGenOptions {
  /** Per-roll focus adherence; defaults to the act's TUNING value. */
  focusBias?: number
  /**
   * Act-aware final requirement bands, keyed `act1`..`actN` then tier.
   * The raw (unrounded) total is clamped to the band and then rounded
   * ONCE — "clamp late, round once". `null` disables clamping entirely
   * (for experiments); undefined uses TUNING.missions.finalReqBands.
   */
  totalReqBands?: Record<string, Partial<Record<MissionTier, readonly [number, number]>>> | null
  /**
   * Ascension pressure (v0.80): A2/A8 each add +10% to requirements and
   * damage. The game passes ascensionPressureMult(); sims run at 1.
   */
  pressureMult?: number
  /** 🚩 v6.00 Warlord's Banner: force this roll to be the named champion. */
  forceChampion?: boolean
  /** 🗓️⚗️ v5.66: the run's weekly modifier id (data/weeklyMods) — rush,
   * fog, flood, freshlegs, headstart and prisma reshape generation. */
  weeklyMod?: string | null
  /** 🎯 v7.29: the mission's index on its node (0 = the first spawn). On an
   * act-1 node posting two threats, the opening wave covers both. */
  spawnIndex?: number
  /** 🎓 v7.38 (per Javi): a SCRIPTED mission (the practice's lessons)
   * posts exactly these asks, so its name, its art and the codex sighting
   * follow the lesson's own element - no Puddle Slimes asking for 🔥. */
  required?: Partial<Record<ElementKey, number>>
  /** 📱 M3 (decision 0.9): a scripted mission's pinned foe (the practice's
   * lessons, data/firstDispatch.ts) - its art ships in the apps. */
  name?: string
  /** 🎓 v7.41 (per Javi, D8): a first run's act-1 rows 0-1 spawn easy-tier
   * missions only (utils/firstRunRails.ts). The tier roll is still made -
   * and then set aside - so every draw after it lands where it always did. */
  easyOnly?: boolean
}

/**
 * Boss minions (v0.52): Dr. Vex's meddling and Basalt's crystals —
 * small side missions on boss fields that never count toward the goal.
 * Scaled lightly by act so they stay quick jobs for one creature.
 */
export function generateMinion(kind: 'scientist' | 'crystal', act = 1): Mission {
  const actIndex = Math.min(Math.max(act, 1), TUNING.acts.scaling.length) - 1
  const scale = TUNING.acts.scaling[actIndex]!
  const total = Math.max(2, Math.round((kind === 'scientist' ? 3 : 4) * scale.reqMult * 0.6 * gauntletReqMult(act)))
  const chosen = shuffle([...ELEMENTS]).slice(0, kind === 'scientist' ? 1 : randInt(1, 2))
  const required = rollRequirements(total, chosen)
  // Meddling is a RELENTLESS drip (v0.80, per Javi — ignoring it was
  // nearly free): short fuse so skipping one bites fast, and a
  // squash-quick fight so engaging stays cheap.
  // 🔥 v7.99 (per Javi): one second off every fuse, these included.
  const expire = kind === 'scientist' ? 9 : 29
  const fight = kind === 'scientist' ? randInt(2, 3) : randInt(4, 6)
  // v3.34 (per Javi: "in act 5 Dr. Vex was still only poking for 6"):
  // the meddling's bite rides the act damage curve, plus a gentle ramp
  // for gauntlet acts past 3 — act 1 ≈ 6, act 2 ≈ 11, act 3 ≈ 17,
  // act 5 ≈ 22. Crystals stay soft: they're chores, not threats.
  const vexPoke = Math.max(6, Math.round(
    6 * scale.damageMult * (1 + 0.15 * Math.max(0, act - TUNING.acts.scaling.length))))
  return {
    id: makeId('m'),
    name: kind === 'scientist' ? 'Dr. Vex\'s Meddling' : 'Power Crystal',
    tier: 'easy',
    minion: kind,
    x: 50,
    y: 100,
    ctrlX: 50,
    ctrlY: 250,
    distance: TUNING.travel.minDistance,
    travelRemaining: 0,
    travelTotal: 0,
    required,
    expireTimer: expire,
    expireTotal: expire,
    fightTimer: fight,
    fightTotal: fight,
    damage: kind === 'scientist' ? vexPoke : 4,
    xp: Math.max(1, Math.round(2 * scale.xpMult * 0.5)),
    slots: kind === 'scientist' ? 1 : 2,
    assignedCreatureIds: [],
    assignedChance: null,
    status: 'waiting',
    removeIn: null,
  }
}

/**
 * 🎓 Training mission (v0.79): a long, riskless side mission — one
 * creature trains for the whole duration and comes home with 1 of 3
 * offered TRAINED SKILLS. No roll, no goal credit, no damage; letting
 * it expire is a lost opportunity, nothing more.
 */
export function generateTrainingMission(skillOptions: string[]): Mission {
  return {
    id: makeId('m'),
    name: 'Training Grounds',
    tier: 'easy',
    training: true,
    trainingOptions: [...skillOptions],
    trainingChoice: null,
    x: 50,
    y: 100,
    ctrlX: 50,
    ctrlY: 250,
    distance: TUNING.travel.minDistance,
    travelRemaining: 0,
    travelTotal: 0,
    required: emptyStats(),
    expireTimer: TUNING.training.expire,
    expireTotal: TUNING.training.expire,
    fightTimer: TUNING.training.fight,
    fightTotal: TUNING.training.fight,
    damage: 0,
    xp: 0,
    slots: 1,
    assignedCreatureIds: [],
    assignedChance: null,
    status: 'waiting',
    removeIn: null,
  }
}

/**
 * Puzzle node (v0.59): missions are BUILT FROM the player's roster so an
 * optimal (or near-optimal) assignment always exists. The roster is
 * partitioned into groups; each mission's requirement is its group's
 * combined base stats — plus act-scaled "excess" that forces chip damage
 * unless the player brings real extra power (surges, relics, levels).
 * Act 1 sometimes hides a perfect (0-damage) board; later acts never do
 * without help (v0.72 hardening).
 */
const PUZZLE_NAMES = ['Trial of Echoes', 'Trial of Mirrors', 'Trial of Shadows', 'Trial of Whispers']

export function generatePuzzleMissions(roster: { id?: string; stats: Stats }[], act = 1): Mission[] {
  const actIndex = Math.min(Math.max(act, 1), TUNING.acts.scaling.length) - 1
  const scale = TUNING.acts.scaling[actIndex]!
  const groupCount = Math.max(2, Math.min(4, roster.length - 1))
  const dealt = shuffle([...roster])
  const groups: { id?: string; stats: Stats }[][] = Array.from({ length: groupCount }, () => [])
  dealt.forEach((creature, index) => groups[index % groupCount]!.push(creature))

  // Hardened in v0.72 (per Javi — puzzles were a free power spike):
  // acts 2–3 ALWAYS overshoot the mirrored stats, and every bump is
  // heavier, so a clean sweep demands surges, relics or overleveled
  // mains. Act 1 rolls per trial (50%), leaving ~1-in-8 boards with a
  // findable perfect split.
  const excessChance = [0.5, 1, 1][actIndex] ?? 1
  return groups.map((group, index) => {
    const required = emptyStats()
    for (const creature of group) {
      for (const element of ELEMENTS) required[element] += creature.stats[element]
    }
    // Imperfection: the trial asks for MORE than the mirrored group can
    // give — chip damage unless the player finds real extra power.
    if (rand() < excessChance) {
      const elements = ELEMENTS.filter(e => required[e] > 0)
      const bumps = 1 + (rand() < 0.5 + actIndex * 0.25 ? 1 : 0)
      for (let b = 0; b < bumps; b++) {
        required[elements[randInt(0, elements.length - 1)]!] += randInt(1 + actIndex, 2 + actIndex * 2)
      }
    }
    const fight = randInt(6, 10)
    return {
      id: makeId('m'),
      name: PUZZLE_NAMES[index] ?? `Trial ${index + 1}`,
      tier: 'medium' as MissionTier,
      puzzle: true,
      x: 50,
      y: 100,
      ctrlX: 50,
      ctrlY: 250,
      distance: TUNING.travel.minDistance,
      travelRemaining: 0,
      travelTotal: 0,
      required,
      // Puzzles have no clock — the assignment IS the challenge.
      expireTimer: 9999,
      expireTotal: 9999,
      fightTimer: fight,
      fightTotal: fight,
      // Varied damage makes the ordering part of the puzzle; raised in
      // v0.72 so sloppy splits genuinely hurt.
      damage: Math.max(3, Math.round(randInt(6, 10) * scale.damageMult * (0.8 + index * 0.2))),
      xp: Math.max(2, Math.round(3 * scale.xpMult)),
      slots: Math.min(5, group.length + 1),
      // The generating group IS the intended answer (v0.74): revealed in
      // the end-of-node summary.
      puzzleAnswer: group.map(c => c.id).filter((id): id is string => !!id),
      assignedCreatureIds: [],
      assignedChance: null,
      status: 'waiting',
      removeIn: null,
    }
  })
}

/**
 * 🧩 v7.77 (per Javi: "puzzle success should be intended on if the split matched the optimal split, not
 * necessarily if damage was taken or not"): does a trial's team add up to its intended group, element by element?
 * Equal sums are an equally good split - same-stat creatures swap freely, and with no excess any split that covers
 * every trial fully has exactly these sums. An answer creature that cannot be sent right now (benched or resting)
 * leaves the answer's sums, so it never makes a board unwinnable.
 */
export function puzzleTeamMatchesAnswer(
  teamIds: readonly string[],
  answerIds: readonly string[],
  roster: readonly { id: string; stats: Stats; benched?: boolean; benchSeconds?: number }[],
): boolean {
  const byId = new Map(roster.map(c => [c.id, c]))
  const sum = (ids: readonly string[], sendableOnly: boolean): Stats => {
    const total = emptyStats()
    for (const id of ids) {
      const creature = byId.get(id)
      if (!creature || (sendableOnly && (creature.benched || (creature.benchSeconds ?? 0) > 0))) continue
      for (const element of ELEMENTS) total[element] += creature.stats[element]
    }
    return total
  }
  const team = sum(teamIds, false)
  const answer = sum(answerIds, true)
  return teamIds.length > 0 && ELEMENTS.every(element => team[element] === answer[element])
}

/** ⚔️ Gauntlet requirement ramp (v2.34; two-stage v4.38): acts past
 * the scaling table compound per extra act — act 4 at the classic
 * rate, act 5+ steeper. 1 for acts 1..3. */
export function gauntletReqMult(act: number): number {
  return gauntletRampMult(act - TUNING.acts.scaling.length)
}

/** ⚔️ v3.35: damage compounds through the gauntlet too — gentler than
 * the requirement ramp (see TUNING.acts.gauntletDamageRampPerAct).
 * 1 for acts 1..3; bosses never use it. */
export function gauntletDamageMult(act: number): number {
  const extra = Math.max(0, act - TUNING.acts.scaling.length)
  return TUNING.acts.gauntletDamageRampPerAct ** extra
}

export function generateMission(node: MapNode, act = 1, dangerMult = 1, opts?: MissionGenOptions): Mission {
  const nodeCfg = TUNING.encounters.byNodeType[node.type as CombatNodeType]
  // 🚩 v6.00: a forced champion is a HARD mission by definition.
  const rolled = opts?.forceChampion ? 'hard' : rollTier(node)
  // 🎓 v7.41 (per Javi, D8): "Easy" means easy on a first run's first rows.
  const tier: MissionTier = opts?.easyOnly && rolled !== 'boss' ? 'easy' : rolled
  const t = TUNING.missions.tiers[tier]
  const actIndex = Math.min(Math.max(act, 1), TUNING.acts.scaling.length) - 1
  const scale = TUNING.acts.scaling[actIndex]!
  const gauntletMult = gauntletReqMult(act)
  // Farther sites host harder, richer missions; the boss is exempt.
  const danger = tier === 'boss' ? 1 : dangerMult
  // Within-act row ramp: each act opens at its baseline and climbs row
  // by row (applied after the band clamp). REQUIREMENTS stay boss-exempt
  // (per Javi: "boss requirements are scaling perfectly"), but v3.45
  // brings bosses into the DAMAGE ramp — a boss sits on its act's last
  // row, so it now hits like the deepest fight of that act instead of
  // like the act's opening row.
  const localRow = node.row % TUNING.map.rows
  const rowReqMult = tier === 'boss' ? 1 : 1 + localRow * TUNING.acts.rowRamp.reqPerRow
  const rowDamageMult = 1 + localRow * TUNING.acts.rowRamp.damagePerRow
  // 🚪 v5.09 act-entry grace (see TUNING.acts.actEntryEase): a new
  // act's first rows ramp in from the previous act's ceiling instead of
  // stepping past it. Bosses sit on the last row — outside the arrays —
  // so they are naturally exempt.
  const entryCfg = act === 2
    ? TUNING.acts.actEntryEase.act2
    : act === 3
      ? TUNING.acts.actEntryEase.act3
      : act > TUNING.acts.scaling.length
        ? TUNING.acts.actEntryEase.gauntlet
        : null
  const entryReqEase = (tier !== 'boss' && entryCfg?.req[localRow]) || 1
  const entryDamageEase = (tier !== 'boss' && entryCfg?.damage[localRow]) || 1

  const focus = node.preview?.focusElements ?? []
  // ⚔️ Gauntlet spread widener (v4.73; PARKED at 0 in v4.74 — Javi
  // meant taller walls, not wider spreads). Bosses exempt if revived.
  const gauntletElements = tier !== 'boss' && act > TUNING.acts.scaling.length
    ? TUNING.acts.gauntletExtraElements
    : 0
  // 🌈 Prisma (weekly modifier): every non-boss mission posts all five.
  // 🎯 v7.29 (player testing, per Javi): act 1 posted ONE element per
  // easy mission (elements [1,1], extraElements 0) while act 2 posts two
  // on every mission - a cliff - and a two-threat node opened on a
  // single threat 31% of the time (both spawns the same coin flip). Two
  // rules on a two-threat node in act 1: (a) a would-be single roll
  // posts BOTH threats with TUNING.missions.act1SplitChance; (b) the
  // opening wave covers both threats - spawn 0 takes the first posted
  // threat, spawn 1 the second (mapgen shuffles the pair per node, so
  // which comes first varies). Off-focus surprises stay as they were.
  const twoThreats = tier !== 'boss' && actIndex === 0 && focus.length === 2
  const elementCount = Math.min(5, randInt(t.elements[0], t.elements[1]) + scale.extraElements + gauntletElements)
  const splitRoll = twoThreats && elementCount === 1 && rand() < TUNING.missions.act1SplitChance
  let chosen: ElementKey[] = opts?.weeklyMod === 'prisma' && tier !== 'boss'
    ? [...ELEMENTS]
    : splitRoll
      ? [...focus]
      : chooseElements(
          elementCount,
          focus,
          opts?.focusBias ?? TUNING.missions.focusBiasByAct[actIndex] ?? TUNING.missions.focusBias,
        )
  if (twoThreats && chosen.length === 1 && focus.includes(chosen[0]!) && opts?.spawnIndex !== undefined && opts.spawnIndex < 2) {
    chosen = [focus[opts.spawnIndex]!]
  }
  // Off-focus surprises come in easier: the player planned around the
  // posted threats, so an off-threat roll must be a speed bump, not a
  // counter. Eases both requirement and damage (boss is never eased).
  const offFocus = tier !== 'boss' && focus.length > 0 && chosen.some(e => !focus.includes(e))
  const ease = offFocus ? TUNING.missions.offFocusEase : 1

  const rawTotal = randInt(t.totalReq[0], t.totalReq[1]) * scale.reqMult * danger * gauntletMult
  const bands: MissionGenOptions['totalReqBands'] = opts?.totalReqBands !== undefined
    ? opts.totalReqBands
    : TUNING.missions.finalReqBands
  const band = bands?.[`act${actIndex + 1}`]?.[tier]
  // ⚔️ Gauntlet acts scale the band WITH the ramp — the act-3 clamp
  // must never cap the climb.
  const banded = band ? Math.min(band[1] * gauntletMult, Math.max(band[0] * gauntletMult, rawTotal)) : rawTotal
  // Ascension pressure (A2/A8) lands after the band, like the row ramp.
  // 🧱 v5.39: the global wall bump rides the same post-band slot.
  // 🗿 v5.42: titan missions are the WALL — the node is worth two rows
  // of progress, so its asks run titanReqMult heavier.
  const pressure = (opts?.pressureMult ?? 1) * (TUNING.missions.globalReqMult ?? 1)
    * (node.type === 'titan' ? TUNING.missions.titanReqMult : 1)
    // 🗓️⚗️ v5.66: the weekly law rides the same post-band slot.
    * weeklyReqMult(opts?.weeklyMod)
  const total = Math.max(1, chosen.length, Math.round(banded * ease * rowReqMult * entryReqEase * pressure))
  // Boss nodes carry a pre-rolled statline from map generation: the
  // player can preview it exactly, and a respawn is the same boss again.
  const storedBoss = tier === 'boss' ? node.preview?.boss : undefined
  const required = storedBoss
    ? { ...storedBoss.required }
    : opts?.required ? { ...emptyStats(), ...opts.required } : rollRequirements(total, chosen)
  // Later acts tighten expire timers (act pressure ramp). Bosses never
  // expire at all — the fight waits for the player. With the v0.54
  // no-expiry experiment active, NOTHING from this generator expires:
  // the on-screen cap is the only pressure (boss splits/minions keep
  // their timers — they're built elsewhere).
  // EXCEPTION (v0.61): Basalt runs a LONG clock — every crystal you
  // squeeze in weakens it, but let the timer lapse and it reforms at
  // full strength (plus the expiry bite).
  const expire = storedBoss?.skill === 'golem'
    ? GOLEM_EXPIRE_SECONDS
    : storedBoss?.skill === 'scientist'
      ? SCIENTIST_EXPIRE_SECONDS
      : tier === 'boss' || !TUNING.encounters.expiryEnabled
      ? 9999
      // 🔥 v7.99 (per Javi: "reduce expiration timers across the board by 1 sec"):
      // the second comes off AFTER the multipliers, so it is a whole second in
      // every act and under every weekly law; the floor keeps a fuse playable.
      : Math.max(5, Math.round(randInt(t.expire[0], t.expire[1]) * nodeCfg.expireMult * scale.expireMult
        // 🗓️⚗️ v5.66: rush halves the clocks, the flood lengthens them,
        // fresh legs trims them a quarter.
        * weeklyExpireMult(opts?.weeklyMod)) - 1)
  // Some bosses drag the fight out: the Amalgam is a WAY longer grind
  // (v0.63 — the meddling piles up while you're stuck in there).
  // Sanguis fights QUICK (v0.65) — its pressure is the blood price, not
  // the clock. The Sandman too (v0.70): his game is the rotating sleep
  // lockout. And Hexa (v0.75): the cursed first slot is a team-building
  // puzzle, not a wait. Basalt fights at normal pace since v1.00 (per
  // Javi — the 100s fuse is the pressure; ×2.5 dragged).
  // Gorge and Yin & Yang were slogs (v1.18, per Javi): Gorge's swelling
  // requirement forces many dispatches, and Yin & Yang stacks the boss
  // fight PLUS two half-fights (each 0.8x the parent), so it ran ~2.6x a
  // normal fight. Both now fight at 0.6x -- and since the halves derive
  // from the parent's fightTotal, trimming the parent cascades to them.
  // Janus joined the quick club in v1.43 (per Javi: "does not need to be
  // that long") — the feint is a planning puzzle, not a clock.
  // Myriad and Umbrax joined the brisk club in v1.74 (per Javi): the
  // fracture already multiplies into six extra piece-fights, and the
  // eclipse's pressure is the rotating lockout, not the total. Dr. Vex
  // went the other way — his fight needed to be LONGER (4 → 5.5×; the
  // meddling minions eat much of the raw total).
  // Faust joined them in v3.81, on the same words Janus got ("does not
  // need to be that long"): its entire mechanic is spent BEFORE the fight
  // starts — you pick a curse, and then there is nothing left to do but
  // watch a timer you can no longer influence.
  // Vessk joined in v6.28 (per Javi: "boss fight not so long"): the
  // blight is a commitment puzzle solved at dispatch, like Faust's curse.
  const QUICK_BOSSES = ['bloodprice', 'knockout', 'cursed', 'feint', 'curse', 'corrupted']
  const BRISK_BOSSES = ['unstable', 'yinyang', 'fracture', 'eclipse']
  const fightMult = storedBoss?.skill === 'scientist'
    ? 5.5
    : QUICK_BOSSES.includes(storedBoss?.skill ?? '') ? 0.5
      : BRISK_BOSSES.includes(storedBoss?.skill ?? '') ? 0.6 : 1
  const fight = Math.round(randInt(t.fight[0], t.fight[1]) * fightMult)

  // Boss skills ride along from the pre-rolled preview; the rotating
  // skills (eclipse / unstable) start on a random required element.
  const skillFields: Partial<Mission> = {}
  if (storedBoss?.skill) {
    skillFields.bossSkill = storedBoss.skill
    const requiredElements = ELEMENTS.filter(e => required[e] > 0)
    const randomRequired = () => requiredElements[randInt(0, requiredElements.length - 1)]
    if (storedBoss.skill === 'corrupted') skillFields.corruptedElement = storedBoss.skillElement ?? randomRequired()
    if (storedBoss.skill === 'feint') skillFields.feintElement = storedBoss.skillElement ?? randomRequired()
    if (storedBoss.skill === 'eclipse') skillFields.eclipsedElement = randomRequired()
    if (storedBoss.skill === 'unstable') {
      skillFields.unstableElement = randomRequired()
      skillFields.unstableGrowIn = UNSTABLE_GROW_SECONDS
      skillFields.unstableRotateIn = UNSTABLE_ROTATE_SECONDS
    }
    if (storedBoss.skill === 'fracture') skillFields.fractureDepth = 0
  }

  return {
    id: makeId('m'),
    // 🧟 v3.86 (per Javi: "Dr. Vex is the mad scientist doing the poking
    // damage; the amalgamation is the big boss in the middle"): the codex,
    // the act reveal and the skill card all wear Vex's name over Vex's
    // PORTRAIT — but the field mission wears the amalgam's ART, so it
    // takes the monster's name. The meddling minions stay his.
    name: storedBoss?.skill === 'scientist'
      ? 'The Amalgamation'
      : storedBoss?.name ?? pinnedName(rollName(tier, required, node.type, opts?.forceChampion), opts?.name),
    ...skillFields,
    tier,
    x: 50,
    y: 100,
    ctrlX: 50,
    ctrlY: 250,
    distance: TUNING.travel.minDistance,
    travelRemaining: 0,
    travelTotal: 0,
    required,
    expireTimer: expire,
    expireTotal: expire,
    fightTimer: fight,
    fightTotal: fight,
    // v3.34 (per Javi): a per-row FLOOR keeps deep-row low rolls honest —
    // a row-6 "easy" can no longer poke for 2. Bosses roll far above any
    // floor, so it stays a regular-mission guard.
    // v3.35: gauntlet acts ramp damage too (the floor rides the ramp so
    // an act-8 row-2 mission can't poke for 6 anymore).
    // v3.45 (per Javi: "bosses need to scale damage per row it's on"):
    // bosses now take BOTH the row ramp and the gauntlet ramp, so a deep
    // act's boss hits like the depth it guards.
    damage: storedBoss?.damage
      ?? Math.max(
        tier === 'boss'
          ? 1
          : Math.max(
            Math.round((1 + Math.round(localRow * TUNING.acts.rowRamp.damageFloorPerRow)) * gauntletDamageMult(act)),
            // 🪚 v7.21 (per Javi): the act's own easy-tier minimum at this row
            // (TUNING.acts.damageFloorOfEasyMin) - the sawtooth's troughs rise
            // with the acts instead of falling back to act-1 pokes.
            Math.round(TUNING.acts.damageFloorOfEasyMin * TUNING.missions.tiers.easy.damage[0]
              * scale.damageMult * rowDamageMult * gauntletDamageMult(act)),
          ),
        Math.round(randInt(t.damage[0], t.damage[1]) * scale.damageMult * danger * ease * rowDamageMult * entryDamageEase
          * pressure * gauntletDamageMult(act)),
      ),
    // 🗿 v5.42: titan pays richer XP (worth two rows of progress).
    xp: storedBoss?.xp ?? Math.max(1, Math.round(randInt(t.xp[0], t.xp[1]) * scale.xpMult * danger
      * (node.type === 'titan' ? TUNING.missions.titanXpMult : 1))),
    // 🤺 v5.42 Duelist's Code: missions accept at most TWO creatures —
    // capped at the printed slots so the card is honest. (An Apotheosis
    // Sylph widening the mission is legitimate counter-tech.)
    slots: storedBoss?.slots ?? Math.min(node.modifier === 'duelist' || node.modifier2 === 'duelist' ? 2 : 9, randInt(t.slots[0], t.slots[1])),
    assignedCreatureIds: [],
    assignedChance: null,
    status: 'waiting',
    removeIn: null,
  }
}
